// Dialogo para vincular una o mas unidades EXISTENTES a un usuario de TRS
// Rastreo directamente (sin pasar por un grupo) — usa la misma API de
// usuariovehiculo que ya consume TrsVehiculoFormDialog al crear un vehiculo
// nuevo, pero aca el vehiculo ya existe: se elige de un listado paginado
// (listarVehiculos, con busqueda y paginado server-side — a diferencia de
// listarVehiculosSimple, que trae TODO de una vez y no escala bien cuando
// hay muchas unidades).
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { listarVehiculos, vincularUsuarioVehiculo } from "../services/trsRastreoService";

export default function TrsAsignarVehiculoDialog({ open, usuario, onClose, onSaved }) {
  const [vehiculos, setVehiculos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [loading, setLoading] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [buscarActivo, setBuscarActivo] = useState("");
  const [selected, setSelected] = useState(new Map()); // id -> { id, veh_placa, veh_alias, veh_modid }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setBuscar("");
    setBuscarActivo("");
    setPage(0);
    setSelected(new Map());
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setBuscarActivo(buscar.trim());
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar, open]);

  const cargar = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await listarVehiculos({ page: page + 1, pageSize, buscar: buscarActivo });
      const body = res?.data;
      const data = body?.data ?? [];
      setVehiculos(Array.isArray(data) ? data : []);
      setTotal(Number(body?.total || 0));
    } catch (e) {
      setVehiculos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, page, pageSize, buscarActivo]);

  useEffect(() => { cargar(); }, [cargar]);

  // Ya vinculados directamente a este usuario — se excluyen para no duplicar
  // el vinculo (los que estan asignados via grupo si pueden volver a elegirse).
  const yaVinculadosIds = new Set((usuario?.vehiculos_relacionados || []).map((v) => String(v.id)));
  const filaSeleccionable = (v) => !yaVinculadosIds.has(String(v.id));

  const toggle = (v) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = String(v.id);
      if (next.has(key)) next.delete(key);
      else next.set(key, v);
      return next;
    });
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    const ids = [...selected.keys()];
    if (!ids.length) {
      const msg = "Selecciona al menos una unidad.";
      setError(msg);
      setSnack({ open: true, msg });
      return;
    }
    setSaving(true);
    setError("");
    try {
      await Promise.all(
        ids.map((vehId) =>
          vincularUsuarioVehiculo({ usu_id: Number(usuario.id), veh_id: Number(vehId), usuveh_activo: true })
        )
      );
      // Se espera a que el padre termine de recargar el arbol antes de cerrar
      // (setSaving sigue en true, spinner visible) — si no, el dialogo se cierra
      // antes de que el cambio se refleje y el usuario ve el arbol viejo un rato.
      await onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo asignar la(s) unidad(es).";
      setError(msg);
      setSnack({ open: true, msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>
          Asignar vehiculo a {usuario?.usu_username || usuario?.usu_nombre || "usuario"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1, overflowY: "visible" }}>
          {!!error && <Alert severity="error">{error}</Alert>}
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar por placa, alias o modem..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            disabled={saving}
            InputProps={{ endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Unidades</Typography>
            <Chip size="small" label={`${selected.size} seleccionada(s)`} sx={{ height: 20, fontSize: "0.68rem" }} />
          </Box>
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, height: 320, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : vehiculos.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5, fontStyle: "italic" }}>
                Sin unidades para mostrar.
              </Typography>
            ) : (
              vehiculos.map((v) => {
                const seleccionable = filaSeleccionable(v);
                const checked = selected.has(String(v.id));
                return (
                  <Box
                    key={v.id}
                    onClick={() => seleccionable && toggle(v)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.4,
                      cursor: seleccionable ? "pointer" : "not-allowed",
                      opacity: seleccionable ? 1 : 0.45,
                      "&:hover": seleccionable ? { bgcolor: "action.hover" } : undefined,
                    }}
                  >
                    <Checkbox size="small" checked={checked} disabled={!seleccionable || saving} sx={{ p: 0.25 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: checked ? 900 : 600 }}>
                        {v.veh_placa || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {seleccionable
                          ? [v.veh_alias, v.veh_modid].filter(Boolean).join(" · ") || "-"
                          : "Ya vinculado a este usuario"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[15, 30, 50]}
            labelRowsPerPage="Limite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
            sx={{ "& .MuiToolbar-root": { pl: 0, pr: 0, minHeight: 40 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
